// Money helpers. The amount columns in our schema (base_price_cents,
// total_cents, price_cents, etc.) store the value in the currency's smallest
// addressable unit. For most currencies that's "cents" (USD/SAR/AED/EGP,
// divisor 100). For Iraqi dinar fils are out of circulation — 1 IQD = the
// minor unit (divisor 1). KWD/JOD use 1000 (mils).
//
// Use these helpers for ALL price formatting + form input so the UI, the AI
// prompt, and the order receipts agree.

import type { Currency } from './common.js';

const DIVISORS: Record<Currency, number> = {
  USD: 100,
  IQD: 1,
  SAR: 100,
  AED: 100,
  EGP: 100,
  JOD: 1000,
};

export function minorDivisor(currency: Currency): number {
  return DIVISORS[currency] ?? 100;
}

/**
 * Convert a stored minor-unit amount into a display string in the major unit.
 *   formatMoney(12000, 'IQD')  → "12,000 IQD"
 *   formatMoney(4500,  'USD')  → "45.00 USD"
 *   formatMoney(12500, 'JOD')  → "12.500 JOD"
 */
export function formatMoney(amountMinor: number, currency: Currency): string {
  const div = minorDivisor(currency);
  const major = amountMinor / div;
  const decimals = div === 1 ? 0 : div === 1000 ? 3 : 2;
  return `${major.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })} ${currency}`;
}

/**
 * Convert a major-unit value the merchant typed in the form into the minor
 * unit we persist.
 *   toMinor(12000, 'IQD') → 12000
 *   toMinor(45,    'USD') → 4500
 *   toMinor(12.5,  'JOD') → 12500
 */
export function toMinor(amountMajor: number, currency: Currency): number {
  return Math.round(amountMajor * minorDivisor(currency));
}
